import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import type { IPageDidMountHandler, BrowserWindow } from '@wix/thunderbolt-symbols'
import {
	CurrentRouteInfoSymbol,
	FeatureStateSymbol,
	PageFeatureConfigSymbol,
	BrowserWindowSymbol,
} from '@wix/thunderbolt-symbols'
import type { AutoDisplayLightboxPageConfig, AutoDisplayLightboxFeatureState } from './types'
import { name } from './symbols'
import type { ILightbox } from 'feature-lightbox'
import { LightboxSymbol } from 'feature-lightbox'
import type { IFeatureState } from 'thunderbolt-feature-state'
import type { ICurrentRouteInfo } from 'feature-router'

const getLocaleDate = (timeZone: string, dateValue: string = '') => {
	const [regionAndCity] = timeZone.split(' ') // City/Region (GMT+TIME)
	const date = dateValue ? new Date(dateValue) : new Date()
	return date.toLocaleString('en-US', { timeZone: regionAndCity })
}

const isCurrentDateInRange = (startDate: string, endDate: string, timeZone: string) => {
	const start = getLocaleDate(timeZone, startDate)
	const end = getLocaleDate(timeZone, endDate)
	const current = new Date(getLocaleDate(timeZone))

	return current >= new Date(start) && current <= new Date(end)
}

export const autoDisplayLightboxFactory = (
	{ triggerPageId, popupId, delay, startDate, endDate, timeZone }: AutoDisplayLightboxPageConfig,
	featureState: IFeatureState<AutoDisplayLightboxFeatureState>,
	currentRouteInfo: ICurrentRouteInfo,
	window: BrowserWindow,
	popupsFeature?: ILightbox
): IPageDidMountHandler => {
	const shouldShowPopupInSession = (): boolean => {
		const sessionKey = `wix-auto-popup-shown-${popupId}`

		const navigationEntries = window?.performance?.getEntriesByType('navigation') as unknown as
			| Array<PerformanceNavigationTiming>
			| undefined
		const isPageReload = navigationEntries?.some((entry) => entry.type === 'reload')

		if (!isPageReload && window?.sessionStorage?.getItem(sessionKey)) {
			return false
		}

		return true
	}

	const markPopupAsShownInSession = (): void => {
		const sessionKey = `wix-auto-popup-shown-${popupId}`
		window?.sessionStorage?.setItem(sessionKey, 'true')
	}

	return {
		pageDidMount() {
			if (!popupsFeature) {
				return
			}
			const { activeTimeout, openLightbox } = featureState.get() || {}
			const isTargetPopupPage = popupsFeature.isLightbox(popupId)
			const isClosed = !openLightbox?.[popupId]

			const hasSchedule = startDate && endDate && timeZone
			const isInSchedule = hasSchedule && isCurrentDateInRange(startDate, endDate, timeZone)
			const canOpenPopup = !hasSchedule || isInSchedule
			const shouldShowInSession = shouldShowPopupInSession()

			if (isTargetPopupPage && canOpenPopup && isClosed && shouldShowInSession) {
				// in case of navigation back to a page with auto display lightbox, clear previous timer
				// to avoid opening lightbox several times
				if (activeTimeout) {
					clearTimeout(activeTimeout)
					featureState.update((currentState) => {
						delete currentState.activeTimeout
						return { ...currentState }
					})
				}

				const nextTimeout = setTimeout(() => {
					const currentPageId = currentRouteInfo.getCurrentRouteInfo()?.pageId
					const isTriggerPageCurrentPage = triggerPageId === currentPageId

					if (isTriggerPageCurrentPage || triggerPageId === 'masterPage') {
						void popupsFeature.open(popupId)
						featureState.update((state) => ({
							...state,
							openLightbox: { ...state.openLightbox, [popupId]: true },
						}))
						markPopupAsShownInSession()
					}
				}, delay * 1000)

				featureState.update((currentState) => ({ activeTimeout: nextTimeout, ...currentState }))
			}
		},
	}
}

export const AutoDisplayLightbox = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(FeatureStateSymbol, name),
		CurrentRouteInfoSymbol,
		BrowserWindowSymbol,
		optional(LightboxSymbol),
	],
	autoDisplayLightboxFactory
)
